import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { uiText } from "@/lib/i18n/uiText";

type Checkin = { claimed: boolean; streak: number; longest: number; reward: number; bonus?: number };

/** Daily check-in: +1 credit a day, +2 on day 3 and +5 on day 7 of each week-long streak. */
export function DailyStreak() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const ran = useRef<string | null>(null);
  const checkin = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("daily_checkin");
      if (error) throw error;
      return data as unknown as Checkin;
    },
    onSuccess: (r) => {
      if (!r?.claimed) return;
      qc.invalidateQueries({ queryKey: ["credit-wallet"] });
      qc.invalidateQueries();
      const total = r.reward + (r.bonus ?? 0);
      toast.success(uiText("Day {{v0}} streak · +{{v1}} credits", { v0: r.streak, v1: total }), {
        description: r.bonus ? uiText("Streak bonus unlocked. Come back tomorrow to keep it going.") : uiText("Come back tomorrow to grow your streak."),
      });
    },
  });

  useEffect(() => {
    if (user && ran.current !== user.id) {
      ran.current = user.id;
      checkin.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const streak = checkin.data?.streak ?? 0;
  if (!user || streak < 1) return null;
  const next = streak % 7 < 3 ? 3 - (streak % 7) : 7 - (streak % 7);
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 text-xs font-medium"
      title={uiText("{{v0}}-day streak · bonus in {{v1}} day(s)", { v0: streak, v1: next })}
      aria-label={uiText("{{v0}}-day streak", { v0: streak })}
    >
      <Flame className="h-3.5 w-3.5 text-primary" aria-hidden />
      {streak}
    </span>
  );
}
